import type { ReactNode } from 'react';
import type { VenueChoice, VenueId } from '@/api/types';
import { cn } from '@/lib/cn';
import { venueOf, venueVars } from './catalog';
import './venues.css';

/**
 * The decorative frame around the call stage (SPEC_V9 §B1): ornaments on the
 * edges only — a tin roof and rain, fairy lights and a skyline, waves and a
 * low sun, shelves and a pendant lamp, a starry sky and a campfire, candles.
 * No background segmentation, nothing over the middle of the picture, no
 * pointer events; animations stop under prefers-reduced-motion.
 */

/** Deterministic pseudo-random positions (stable across renders). */
function seeded(n: number, seed: number): number[] {
  const out: number[] = [];
  let x = seed;
  for (let i = 0; i < n; i++) {
    x = (x * 9301 + 49297) % 233280;
    out.push(x / 233280);
  }
  return out;
}

function TinRoof() {
  return (
    <>
      <svg className="absolute inset-x-0 top-0 h-7 w-full" viewBox="0 0 400 28" preserveAspectRatio="none" aria-hidden>
        <path d="M0 0H400V16Q395 26 390 16T380 16T370 16T360 16T350 16T340 16T330 16T320 16T310 16T300 16T290 16T280 16T270 16T260 16T250 16T240 16T230 16T220 16T210 16T200 16T190 16T180 16T170 16T160 16T150 16T140 16T130 16T120 16T110 16T100 16T90 16T80 16T70 16T60 16T50 16T40 16T30 16T20 16T10 16T0 16Z" fill="#3F5F59" />
        <path d="M0 6H400" stroke="#F47B2A" strokeWidth="1.5" />
      </svg>
      <div className="venue-rain" />
      <svg className="absolute bottom-3 left-3 h-16 w-14 sm:h-20 sm:w-16" viewBox="0 0 56 70" aria-hidden>
        <path className="venue-steam" d="M22 22c-4-6 4-9 0-15" stroke="#F3E6D6" strokeOpacity="0.7" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path className="venue-steam" style={{ animationDelay: '1.2s' }} d="M32 22c-4-6 4-9 0-15" stroke="#F3E6D6" strokeOpacity="0.6" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M10 30h36l-5 34a6 6 0 0 1-6 5H21a6 6 0 0 1-6-5Z" fill="#B5612F" />
        <path d="M10 30h36l-1 6H11Z" fill="#8E4321" />
        <ellipse cx="28" cy="30" rx="18" ry="3.2" fill="#D9A066" />
      </svg>
    </>
  );
}

function FairyLights() {
  const n = 16;
  return (
    <>
      <svg className="absolute inset-x-0 top-0 h-12 w-full" viewBox="0 0 1000 48" preserveAspectRatio="none" aria-hidden>
        <path d="M0 4Q500 70 1000 4" stroke="#FFE7B0" strokeOpacity="0.4" strokeWidth="2" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      {Array.from({ length: n - 1 }, (_, i) => {
        const t = (i + 1) / n;
        return (
          <span
            key={i}
            className="venue-twinkle absolute h-2.5 w-2.5 -translate-x-1/2 rounded-full"
            style={{
              left: `${t * 100}%`,
              top: `${4 + 66 * t * (1 - t) * 0.5}px`,
              background: i % 4 === 0 ? '#F28DB2' : '#F2C21A',
              animationDelay: `${(i * 0.37) % 2.6}s`,
            }}
          />
        );
      })}
      <svg className="absolute inset-x-0 bottom-0 h-14 w-full opacity-90" viewBox="0 0 400 56" preserveAspectRatio="none" aria-hidden>
        <path d="M0 56V30h18V18h14v12h10V8h20v22h12V22h16v34Zm92 0V26h16V12h22v18h10V20h14v36Zm62 0V34h12V16h18v40Zm32 0V24h22V4h14v20h12V30h18v26Zm68 0V28h14V14h20v42Zm36 0V20h16V10h12v18h16v28Zm46 0V32h14v-8h18v32Z" fill="#090722" />
      </svg>
      {seeded(18, 7).map((r, i) => (
        <span key={i} className="absolute bottom-1 h-1 w-1 bg-[#FFD27A]" style={{ left: `${(i / 18) * 100 + r * 3}%`, bottom: `${6 + ((r * 1000) % 30)}px`, opacity: 0.55 }} />
      ))}
    </>
  );
}

function Waves() {
  return (
    <>
      <div className="absolute -bottom-12 -right-12 h-36 w-36 rounded-full bg-[#F47B2A]" />
      <div className="absolute inset-x-0 bottom-0 h-12 overflow-hidden">
        <svg className="venue-wave absolute bottom-0 left-0 h-12 w-[200%]" viewBox="0 0 800 48" preserveAspectRatio="none" aria-hidden>
          <path d="M0 22Q50 10 100 22T200 22T300 22T400 22T500 22T600 22T700 22T800 22V48H0Z" fill="#FFE3CC" fillOpacity="0.16" />
          <path d="M0 32Q50 22 100 32T200 32T300 32T400 32T500 32T600 32T700 32T800 32V48H0Z" fill="#FFF4E6" fillOpacity="0.14" />
        </svg>
      </div>
      <svg className="venue-sway absolute -left-4 -top-3 h-28 w-28 opacity-80" viewBox="0 0 110 110" aria-hidden>
        <path d="M6 6c30 4 52 18 66 40-18-10-40-14-58-10 14 2 28 8 38 16-16-2-34 2-46 10 12-10 32-16 50-14C40 34 22 20 6 6Z" fill="#2A1430" />
      </svg>
    </>
  );
}

function Shelves() {
  const colors = ['#7A3E2E', '#2F5D58', '#C9A27A', '#5B4A7A', '#A8552F', '#3D4F2F', '#E2C48F'];
  const column = (seed: number) =>
    seeded(14, seed).map((r, i) => (
      <span key={i} className="block rounded-[2px]" style={{ height: `${5 + r * 4}%`, marginTop: 2, background: colors[Math.floor(r * colors.length)], opacity: 0.7 + r * 0.2 }} />
    ));
  return (
    <>
      <div className="absolute inset-y-6 left-0 flex w-2.5 flex-col sm:w-3.5">{column(11)}</div>
      <div className="absolute inset-y-6 right-0 flex w-2.5 flex-col sm:w-3.5">{column(29)}</div>
      <svg className="venue-sway absolute left-1/2 top-0 h-16 w-16 -translate-x-1/2" viewBox="0 0 64 64" aria-hidden>
        <path d="M32 0v22" stroke="#C9A27A" strokeOpacity="0.6" strokeWidth="1.5" />
        <path d="M18 34a14 12 0 0 1 28 0Z" fill="#3A281C" stroke="#C9A27A" strokeOpacity="0.5" />
        <ellipse cx="32" cy="36" rx="9" ry="3" fill="#FFD696" />
        <ellipse cx="32" cy="44" rx="22" ry="10" fill="rgba(255,214,150,0.18)" />
      </svg>
      <div className="venue-rain" style={{ opacity: 0.6 }} />
    </>
  );
}

function StarryCamp() {
  return (
    <>
      {seeded(26, 3).map((r, i) => {
        const side = i % 3;
        const left = side === 0 ? r * 100 : side === 1 ? r * 12 : 88 + r * 12;
        const top = side === 0 ? (r * 997) % 18 : (r * 991) % 70;
        const s = 1 + ((r * 37) % 2.2);
        return (
          <span
            key={i}
            className="venue-twinkle absolute rounded-full bg-[#E8EEFF]"
            style={{ left: `${left}%`, top: `${top}%`, width: s + 0.5, height: s + 0.5, animationDelay: `${(i * 0.53) % 2.6}s` }}
          />
        );
      })}
      <svg className="absolute right-[8%] top-3 h-12 w-32 opacity-80" viewBox="0 0 128 48" aria-hidden>
        <path d="M4 30L30 12L56 22L84 6L120 18" stroke="#9FB8FF" strokeOpacity="0.55" strokeWidth="1" fill="none" />
        {[
          [4, 30],
          [30, 12],
          [56, 22],
          [84, 6],
          [120, 18],
        ].map(([x, y]) => (
          <circle key={`${x}`} cx={x} cy={y} r="2" fill="#E8EEFF" />
        ))}
      </svg>
      <svg className="absolute bottom-2 left-[9%] h-12 w-14" viewBox="0 0 56 48" aria-hidden>
        <path className="venue-flicker" d="M28 4c8 10 14 16 10 28-2 6-8 10-10 10s-9-3-11-10c-3-10 6-14 11-28Z" fill="#FF9A3C" />
        <path className="venue-flicker" style={{ animationDelay: '0.4s' }} d="M28 18c4 6 7 9 5 16-1 3-3 5-5 5s-5-2-6-5c-1-6 3-9 6-16Z" fill="#FFE08A" />
        <path d="M8 44l40-6M10 38l38 8" stroke="#5A3A22" strokeWidth="4" strokeLinecap="round" />
      </svg>
      <svg className="absolute bottom-0 right-3 h-16 w-20 opacity-90" viewBox="0 0 80 64" aria-hidden>
        <path d="M40 4L76 64H4Z" fill="#0A1028" />
        <path d="M40 4L52 64H28Z" fill="#141E44" />
      </svg>
    </>
  );
}

function Candles() {
  const candle = (cls: string, delay: string) => (
    <svg className={cn('absolute bottom-0 h-24 w-12', cls)} viewBox="0 0 48 96" aria-hidden>
      <ellipse cx="24" cy="30" rx="20" ry="22" fill="rgba(255,179,107,0.25)" />
      <path className="venue-flicker" style={{ animationDelay: delay }} d="M24 14c5 7 7 11 5 17-1 3-3 5-5 5s-4-2-5-5c-2-6 1-10 5-17Z" fill="#FFB347" />
      <path className="venue-flicker" style={{ animationDelay: delay }} d="M24 22c2 3 3 5 2 8-.5 1.5-1 2-2 2s-1.5-.5-2-2c-1-3 0-5 2-8Z" fill="#FFF1C2" />
      <path d="M24 36v4" stroke="#3A2A1A" strokeWidth="1.5" />
      <rect x="14" y="40" width="20" height="56" rx="3" fill="#F6E7CF" />
      <rect x="27" y="40" width="5" height="56" fill="#E4CBAA" />
    </svg>
  );
  return (
    <>
      {candle('left-2 sm:left-4', '0s')}
      {candle('left-12 h-16 w-8 sm:left-16', '0.6s')}
      {candle('right-2 sm:right-4', '0.3s')}
      {seeded(6, 13).map((r, i) => (
        <span key={i} className="absolute h-2.5 w-3.5 rounded-[60%_40%_60%_40%] bg-[#C0283C]" style={{ top: `${4 + r * 10}%`, left: `${i < 3 ? r * 14 : 86 + r * 12}%`, transform: `rotate(${r * 180}deg)`, opacity: 0.7 }} />
      ))}
    </>
  );
}

const ORNAMENTS: Record<VenueId, () => ReactNode> = {
  chai_tapri: TinRoof,
  rooftop_night: FairyLights,
  beach_sunset: Waves,
  bookstore_cafe: Shelves,
  stargazing: StarryCamp,
  candlelight: Candles,
};

export function VenueFrame({ venue, className }: { venue: VenueChoice | null | undefined; className?: string }) {
  const v = venueOf(venue);
  if (!v) return null;
  const Ornament = ORNAMENTS[v.id];
  return (
    <div className={cn('venue-frame', className)} style={venueVars(v)} data-testid="venue-frame" data-venue={v.id} aria-hidden>
      <Ornament />
    </div>
  );
}
